import { formatRain, rainTotalHex } from "./rainLayer.js";

const WET_MM = 1.27; // 0.05 in: below this for both, call it a dry match

// Gauges are filled with their own measured total on the rain map's palette,
// so a gauge that blends into the surrounding map agrees with the radar.
// Dry gauges are hollow; incomplete ones are faded gray.
export function gaugeStyle(row) {
  if (row.status !== "ok") return { fillColor: "#556677", fillOpacity: 0.35, color: "#8899aa", dashArray: "2 2" };
  const fill = rainTotalHex(row.mm);
  return fill
    ? { fillColor: fill, fillOpacity: 1, color: "#0a0e17", dashArray: null }
    : { fillColor: "#0a0e17", fillOpacity: 0, color: "#e0e6f0", dashArray: null };
}

// Radar/gauge ratio of summed totals over gauges where either saw rain.
export function gaugeBias(rows) {
  let r = 0;
  let g = 0;
  let n = 0;
  for (const row of rows) {
    if (row.status !== "ok" || !Number.isFinite(row.radarMm) || Math.max(row.radarMm, row.mm) < WET_MM) continue;
    r += row.radarMm;
    g += row.mm;
    n++;
  }
  return { n, ratio: g > 0 ? r / g : NaN };
}

function formatTime(ms, timeZone) {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(ms));
}

export function createGaugeLayer(L, map, { onPick } = {}) {
  const group = L.layerGroup();
  let rows = [];
  let units = "in";
  let timeZone;
  let visible = true;

  function tooltipHtml(row) {
    const head = `<strong>${row.id}</strong> ${row.name || ""}`;
    if (row.status !== "ok") return `${head}<br>Gauge data incomplete for this window`;
    const window = `${formatTime(row.w0, timeZone)} – ${formatTime(row.w1, timeZone)}`;
    return `${head}<br>Gauge ${formatRain(row.mm, units)} · Radar ${formatRain(row.radarMm, units)}`
      + `<br><span class="rain-gauge-sub">Same window, ${window}${row.frozen ? " · may include snow/ice" : ""}${row.gapMs > 0 ? " · radar gap" : ""}</span>`;
  }

  function draw() {
    group.clearLayers();
    for (const row of rows) {
      if (row.status !== "ok" && row.status !== "incomplete") continue;
      L.circleMarker([row.lat, row.lon], {
        radius: 5,
        weight: 1.5,
        ...gaugeStyle(row),
        bubblingMouseEvents: false,
      })
        .bindTooltip(tooltipHtml(row), { direction: "top", offset: [0, -6], className: "rain-readout" })
        .on("click", () => onPick?.(L.latLng(row.lat, row.lon)))
        .addTo(group);
    }
  }

  return {
    show(nextRows, opts = {}) {
      rows = nextRows;
      timeZone = opts.timeZone;
      draw();
      if (visible) group.addTo(map);
    },
    setUnits(next) {
      units = next;
      draw();
    },
    setVisible(next) {
      visible = next;
      if (visible && rows.length) group.addTo(map);
      else map.removeLayer(group);
    },
    clear() {
      rows = [];
      group.clearLayers();
      map.removeLayer(group);
    },
    // Nearest usable gauge within maxKm of (lat, lon), or null.
    nearest(lat, lon, maxKm = 15) {
      let best = null;
      let bestKm = maxKm;
      for (const row of rows) {
        if (row.status !== "ok" || !Number.isFinite(row.radarMm)) continue;
        const km = Math.hypot((row.lat - lat) * 111, (row.lon - lon) * 111 * Math.cos((lat * Math.PI) / 180));
        if (km <= bestKm) {
          best = { ...row, km };
          bestKm = km;
        }
      }
      return best;
    },
    get rows() {
      return rows;
    },
    get visible() {
      return visible;
    },
  };
}
